<template>
  <div style="height: calc(100vh - 100px)" class="w-full overflow-hidden">
    <div class="container flex h-[100%] mx-auto" v-loading="loading">
      <div
        class="min-w-[200px] h-[100%] rounded-[6px]"
        :style="{ background: 'rgba(255, 255, 255, 0.2)' }"
      >
        <div
          class="flex justify-between items-center slide-top h-[60px] px-[20px]"
        >
          <h2 class="text-lg font-bold text-[#ffffff]">环境列表</h2>
          <el-button
            type="primary"
            size="small"
            title="新增环境"
            :icon="Plus"
            @click="showAddEnvDialog = true"
          >
          </el-button>
        </div>
        <div
          v-for="env in environments"
          :key="env.id"
          class="h-[40px] flex justify-between items-center cursor-pointer bg-orange-100 px-[20px]"
          :style="{
            backgroundColor: activeIndex === env.id ? '#f5f7fa' : '',
          }"
          :title="env.name"
          @click="activeIndex = env.id"
        >
          <div
            class="text-[#ffffff] truncate"
            :style="{ color: activeIndex === env.id ? '#409eff' : '' }"
          >
            {{ env.name }}
          </div>
        </div>
      </div>
      <div
        class="h-[100%] rounded-[6px] ml-[12px] bg-[rgba(255,255,255,0.2)] text-[#ffffff]"
        :style="{ width: 'calc(100vw - 260px)' }"
      >
        <div
          class="slide-top flex items-center justify-between h-[60px] px-[20px]"
        >
          <h2 class="text-lg font-bold text-[#ffffff]">环境信息</h2>
          <div v-if="currentEnv">
            <el-button
              :icon="Refresh"
              @click="
                () => {
                  loadEnvironments();
                  activeIndex && loadProjects(activeIndex);
                }
              "
              >刷新</el-button
            >
            <el-popconfirm
              title="确定要部署这个环境下的所有项目吗？"
              confirm-button-text="确定"
              cancel-button-text="取消"
              @confirm="deployEnv(currentEnv.id)"
              width="200"
            >
              <template #reference>
                <el-button type="primary" :icon="Upload"> 一键部署 </el-button>
              </template>
            </el-popconfirm>
            <el-button
              type="primary"
              :icon="Edit"
              @click="openEditEnvDialog(currentEnv)"
            >
              编辑环境
            </el-button>
            <el-popconfirm
              title="确定要销毁这个环境吗？"
              confirm-button-text="确定"
              cancel-button-text="取消"
              @confirm="removeEnvironment(currentEnv.id)"
              width="200"
            >
              <template #reference>
                <el-button type="danger" :icon="Delete"> 销毁环境 </el-button>
              </template>
            </el-popconfirm>
          </div>
        </div>
        <div v-if="currentEnv" class="p-[12px]">
          <el-descriptions
            :column="2"
            direction="vertical"
            :border="true"
            style="background-color: rgba(255, 255, 255, 0.7)"
          >
            <el-descriptions-item label="环境名称"
              >{{ currentEnv?.name }}
              <el-tag>{{ currentEnv?.eName }}</el-tag></el-descriptions-item
            >
            <el-descriptions-item label="默认分支">{{
              currentEnv?.branch
            }}</el-descriptions-item>
            <el-descriptions-item label="工作路径">{{
              currentEnv?.workPath
            }}</el-descriptions-item>
            <el-descriptions-item label="环境变量">
              <el-button
                type="primary"
                size="small"
                :icon="Setting"
                @click="showEnvVars = true"
              >
                环境变量
              </el-button>
            </el-descriptions-item>
          </el-descriptions>

          <div class="mt-[30px]">
            <div class="flex items-center justify-between mb-[12px]">
              <h3 class="text-lg font-bold">
                <el-radio-group v-model="activeProjectType">
                  <el-radio-button label="build">构建项目</el-radio-button>
                  <el-radio-button label="image">镜像项目</el-radio-button>
                </el-radio-group>
              </h3>
              <el-button type="primary" @click="openAddProject" :icon="Plus">
                新增项目
              </el-button>
            </div>
            <ProjectList
              v-if="currentEnv"
              :type="activeProjectType"
              :projects="currProjects"
              :loading="projectLoading"
              @deploy="deployProject"
              @destroy="destroyProject"
              @view="openProjectDetail"
              @edit="handleEditProject"
            />
          </div>
        </div>
        <div v-else>
          <el-empty description="暂无环境" />
        </div>
      </div>
    </div>

    <AddEnvDialog
      v-model:visible="showAddEnvDialog"
      :env-data="editEnvData"
      @success="loadEnvironments"
    />

    <AddProjectDialog
      v-if="currentEnv"
      v-model:visible="showAddProjectDialog"
      :type="activeProjectType"
      :env-id="currentEnv.id"
      :project="currentEditingProject"
      @success="handleProjectChange"
    />

    <ProjectDetailDialog
      v-model:visible="showProjectDetailDialog"
      :project="currentProject"
      :env-name="currentProject ? getEnvName(currentProject.envID) : ''"
    />

    <el-dialog v-model="showEnvVars" title="环境变量" width="600px" draggable>
      <el-table
        :data="currentEnv?.envMaps || []"
        stripe
        :border="true"
        style="background-color: rgba(255, 255, 255, 0.7)"
      >
        <el-table-column
          prop="key"
          label="变量名"
          min-width="200"
        ></el-table-column>
        <el-table-column
          prop="value"
          label="变量值"
          min-width="250"
        ></el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import {
  Plus,
  Upload,
  Delete,
  Setting,
  Refresh,
  Edit,
} from "@element-plus/icons-vue";
import { useEnvironments } from "../composables/useEnvironments";
import { useProjects } from "../composables/useProjects";
import ProjectList from "./ProjectList.vue";
import AddEnvDialog from "./AddEnvDialog.vue";
import AddProjectDialog from "./AddProjectDialog.vue";
import ProjectDetailDialog from "./ProjectDetailDialog.vue";
import type { Project, Environment } from "../types";
import { deleteUrlParam, setUrlParam } from "./utils";

const activeProjectType = ref<"build" | "image">("build");

const {
  activeIndex,
  environments,
  loading,
  loadEnvironments,
  removeEnvironment,
  getEnvName,
} = useEnvironments();

const {
  projects,
  loading: projectLoading,
  loadProjects,
  deployEnv,
  deployProject,
  destroyProject,
} = useProjects(activeIndex);

const currProjects = computed(() => {
  return projects.value.filter((p) => p.type === activeProjectType.value);
});

const currentEnv = computed<Environment | null>(() => {
  return activeIndex.value
    ? environments.value.find((e) => e.id === activeIndex.value) || null
    : null;
});
const showAddEnvDialog = ref(false);
const showAddProjectDialog = ref(false);
const showProjectDetailDialog = ref(false);
const showEnvVars = ref(false);
const currentProject = ref<Project | null>(null);
const currentEditingProject = ref<Project | null>(null);

const editEnvData = ref<Environment | null>(null);
const openEditEnvDialog = (env: Environment) => {
  // 打开编辑环境弹窗
  editEnvData.value = {
    ...env,
  };
  showAddEnvDialog.value = true;
};
watch(
  () => showAddEnvDialog.value,
  (newVal) => {
    if (!newVal) {
      editEnvData.value = null;
    }
  },
);

const openAddProject = () => {
  if (currentEnv.value) {
    currentEditingProject.value = null;
    showAddProjectDialog.value = true;
  }
};

const handleEditProject = (project: Project) => {
  currentEditingProject.value = project;
  showAddProjectDialog.value = true;
};

const handleProjectChange = async () => {
  if (currentEnv.value) {
    await loadProjects(currentEnv.value.id);
  }
};

const openProjectDetail = (project: Project) => {
  console.log(project);
  currentProject.value = project;
  showProjectDetailDialog.value = true;
};

onMounted(async () => {
  await loadEnvironments();
});

watch(activeIndex, async (newVal, oldVal) => {
  if (newVal !== oldVal) {
    if (newVal) {
      await loadProjects(newVal);
      setUrlParam("envId", newVal.toString()); // 同步地址栏参数
    } else {
      projects.value = [];
      deleteUrlParam("envId"); // 清除地址栏参数
    }
  }
});
</script>

<style scoped>
.slide-top {
  position: relative;
}
.slide-top:after {
  background-color: #e4e7ed;
  bottom: 0;
  content: "";
  height: 2px;
  left: 0;
  position: absolute;
  width: 100%;
  z-index: 1;
}
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}
</style>
